import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { handleExtract } from "../../Components/Helper";
import { fetchAllPosts } from "../../Services/Slices/allPostsSlice";
import { fetchPublic } from "../../Services/Slices/publicSlice";
import { isLoggedIn } from "../../Auth/auth";
import { Dayjs } from "dayjs";

const initialForm = {
  date: { from: null as Dayjs | null, to: null as Dayjs | null },
  post_type: [] as string[],
  post_origin: [] as string[],
  post_code: "",
  words: [] as string[],
  words_contain: true,
};

export function useHome() {
  const dispatch = useDispatch();
  const response = useSelector((state: any) => state.publicSlice);
  const allPostsResponse = useSelector((state: any) => state.allPostsSlice);
  const deletePublishedResponse = useSelector(
    (state: any) => state.deletePublishedFileSlice,
  );

  const [extracted, setExtracted] = useState<any>([]);
  const [backup, setBackup] = useState<any>({});
  const [page, setPage] = useState<number>(1);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [publishedError, setPublishedError] = useState<boolean>(false);
  const [reversePosts, setReversePosts] = useState<boolean>(false);
  const [hasSearchedBefore, setHasSearchedBefore] = useState<boolean>(true);
  const [tempPage, setTempPage] = useState<number | null>(null);
  const [isResponsive, setIsResponsive] = useState(false);
  const [hasValue, setHasValue] = useState<boolean>(false);
  const [form, setForm] = useState(initialForm);
  const ref = useRef<boolean>();
  const dateFormat = "YYYY-MM-DD";
  const normalizedWords = (words: string[]) =>
    words.map((word) => word.trim().toLowerCase()).filter(Boolean);

  const areFiltersEmpty = (currentForm = form) =>
    !currentForm.date.from &&
    !currentForm.date.to &&
    currentForm.post_type.length === 0 &&
    currentForm.post_origin.length === 0 &&
    !currentForm.post_code &&
    normalizedWords(currentForm.words).length === 0 &&
    !hasValue;

  const handleDisable = () => {
    return areFiltersEmpty();
  };

  const handleDateChange = (newDate: Dayjs | null, type: "from" | "to") => {
    setForm((prev) => ({
      ...prev,
      date: {
        ...prev.date,
        [type]: newDate,
      },
    }));
  };

  const handleSubmitFilters = () => {
    const lowercaseWords = normalizedWords(form.words);
    const { words, post_type, post_origin, ...formWithoutWords } = form;
    const updatedRange = {
      ...formWithoutWords,
      ...(post_type.length > 0 ? { post_type } : {}),
      ...(post_origin.length > 0 ? { post_origin } : {}),
      ...(form.date.from || form.date.to
        ? {
            date: {
              from: form.date.from
              ? form.date.from.format(dateFormat)
              : null,
              to: form.date.to
                ? form.date.to.format(dateFormat)
                : form.date.from
            },
          }
        : {}),
      ...(lowercaseWords.length > 0 ? { words: lowercaseWords } : {}),
    };
    if (form.date.from && !form.date.to) {
      updatedRange.date.to = form.date.from.format(dateFormat);
    } else if (!form.date.from && form.date.to) {
      updatedRange.date.from = form.date.to.format(dateFormat);
    }
    dispatch<any>(fetchPublic(updatedRange, "1", reversePosts));
    setPage(1);
    setTempPage(1);
    setBackup(updatedRange);
    setIsSearched(true);
  };

  const handleClearFiltersForm = () => {
    setIsSearched(false);
    setBackup({});
    setForm({
      date: { from: null, to: null },
      post_type: [],
      post_origin: [],
      post_code: "",
      words: [],
      words_contain: true,
    });
    setHasValue(false);
    setPage(1);
    setTempPage(1);
    handleClearFilters();
  };

  const columns = [
    ...(isLoggedIn() ? [{ title: "Fixar", property: "pin" }] : []),
    { title: "Ato", property: "edition" },
    { title: "Origem", property: "origin" },
    { title: "Descrição", property: "title" },
    { title: "Publicado", property: "date" },
    { title: "Status", property: "status" },
    { title: "Visualizar", property: "view" },
    ...(isLoggedIn()
      ? [
          { title: "Editar", property: "revoke" },
          { title: "Excluir", property: "deletePublished" },
        ]
      : []),
  ];

  useEffect(() => {
    if (areFiltersEmpty()) {
      setIsSearched(false);
      setBackup({});
      setPage(1);
      setTempPage(1);
    }
  }, [form, hasValue]);

  useEffect(() => {
    setExtracted([]);
    if (isSearched) {
      ref.current = true;
      handleExtract(response.data.results, setExtracted);
      setHasSearchedBefore(true);
    } else {
      handleExtract(allPostsResponse.data.results, setExtracted);
    }
  }, [isSearched, response.data, allPostsResponse.data]);

  useEffect(() => {
    if (isSearched && page !== tempPage) {
      dispatch<any>(fetchPublic(backup, page.toString(), reversePosts));
      setTempPage(page);
    }
  }, [dispatch, isSearched, backup, page, tempPage, reversePosts]);

  useEffect(() => {
    if (!isSearched) {
      dispatch<any>(fetchAllPosts(page.toString(), false, reversePosts));
    }
  }, [
    deletePublishedResponse.data.response,
    dispatch,
    isSearched,
    page,
    reversePosts,
  ]);

  useEffect(() => {
    if (ref.current && page !== tempPage) {
      dispatch<any>(fetchPublic(backup, page.toString(), reversePosts));
      setTempPage(page);
    }
  }, [dispatch, ref, backup, page, tempPage, reversePosts]);

  useEffect(() => {
    deletePublishedResponse.error
      ? setPublishedError(true)
      : setPublishedError(false);
  }, [deletePublishedResponse.error]);

  useEffect(() => {
    const handleResize = () => {
      setIsResponsive(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleToggleReverse = () => {
    const next = !reversePosts;
    setReversePosts(next);
    if (isSearched || ref.current) {
      dispatch<any>(fetchPublic(backup, page.toString(), next));
    }
  };

  const handleClearFilters = () => {
    setPage(1);
    setIsSearched(false);
    setBackup({});
    dispatch<any>(fetchAllPosts("1", false, reversePosts));
  };

  const tableTitle = isSearched
    ? `Atos encontrados: ${response.data.count ? response.data.count : ""}`
    : undefined;

  const tableTotal = isSearched
    ? response.data.count
    : allPostsResponse.data.count;

  const tableIsEmpty =
    (isSearched && response?.data?.results?.length === 0) ||
    (!isSearched && allPostsResponse?.data?.results?.length === 0);

  const tableLoading = isSearched ? response.loading : allPostsResponse.loading;

  const tableError = isSearched ? response.error : allPostsResponse.error;

  return {
    deletePublishedResponse,
    publishedError,
    setPublishedError,
    response,
    allPostsResponse,
    isSearched,
    setIsSearched,
    hasSearchedBefore,
    isResponsive,
    setBackup,
    setPage,
    setTempPage,
    handleClearFilters,
    handleToggleReverse,
    reversePosts,
    extracted,
    columns,
    page,
    tableTitle,
    tableTotal,
    tableIsEmpty,
    tableLoading,
    tableError,
    form,
    setForm,
    hasValue,
    setHasValue,
    handleDateChange,
    handleDisable,
    handleSubmitFilters,
    handleClearFiltersForm,
  };
}
